import { Button, IconButton, Input, Modal, Textarea, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { FileText, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { CollectionFileSummary, DataCollectionResponse } from "@/api";
import { addNoteMutation, addWebsiteUrlMutation, errorMessage } from "@/api";
import { Text } from "@/components/Text";
import { DataSourceStatusBadge } from "./dataSourceMeta";
import { SharePointPicker, type SharePointSelection } from "./SharePointPicker";

export function FileRow({
  file,
  onDelete,
  deleteLabel,
  onPreview,
  selected = false,
}: Readonly<{
  file: CollectionFileSummary;
  onDelete: () => void;
  deleteLabel: string;
  onPreview?: () => void;
  selected?: boolean;
}>) {
  return (
    <li className={["-mx-2 flex items-center gap-3 rounded-lg px-2 py-2", selected ? "bg-surface-base" : ""].join(" ")}>
      <FileText size={16} aria-hidden className="shrink-0 text-content-tertiary" />
      <div className="flex min-w-0 flex-1 flex-col">
        {onPreview ? (
          <button
            type="button"
            onClick={onPreview}
            aria-current={selected || undefined}
            className="min-w-0 cursor-pointer rounded text-left btn-focus-ring"
          >
            <Text variant="body-sm" className="truncate">
              {file.filename}
            </Text>
          </button>
        ) : (
          <Text variant="body-sm" className="truncate">
            {file.filename}
          </Text>
        )}
        {file.errorMessage && (
          <Text variant="caption" tone="error" className="truncate">
            {file.errorMessage}
          </Text>
        )}
      </div>
      <DataSourceStatusBadge status={file.status} />
      <IconButton icon={<Trash2 />} aria-label={deleteLabel} variant="ghost" size="sm" onClick={onDelete} />
    </li>
  );
}

export function AddUrlModal({
  source,
  open,
  onClose,
  onDone,
}: Readonly<{ source: DataCollectionResponse; open: boolean; onClose: () => void; onDone: () => Promise<unknown> }>) {
  const { t } = useTranslation("dataSources");
  const toast = useToast();
  const [url, setUrl] = useState("");
  const add = useMutation({
    ...addWebsiteUrlMutation(),
    onSuccess: async () => {
      await onDone();
      toast.success(t("content.urlAdded"));
      setUrl("");
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e, t("content.addUrlFailed"))),
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("content.addUrl")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("content.cancel")}
          </Button>
          <Button
            variant="primary"
            loading={add.isPending}
            disabled={!url.trim()}
            onClick={() => add.mutate({ path: { collection_uuid: source.uuid }, body: { url: url.trim() } })}
          >
            {t("content.addUrl")}
          </Button>
        </>
      }
    >
      <Input
        aria-label={t("create.url")}
        placeholder={t("create.urlPlaceholder")}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
    </Modal>
  );
}

export function AddNoteModal({
  source,
  open,
  onClose,
  onDone,
}: Readonly<{ source: DataCollectionResponse; open: boolean; onClose: () => void; onDone: () => Promise<unknown> }>) {
  const { t } = useTranslation("dataSources");
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const add = useMutation({
    ...addNoteMutation(),
    onSuccess: async () => {
      await onDone();
      toast.success(t("content.noteAdded"));
      setTitle("");
      setContent("");
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e, t("content.addNoteFailed"))),
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("content.addNote")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("content.cancel")}
          </Button>
          <Button
            variant="primary"
            loading={add.isPending}
            disabled={!title.trim() || !content.trim()}
            onClick={() =>
              add.mutate({ path: { collection_uuid: source.uuid }, body: { title: title.trim(), content } })
            }
          >
            {t("content.addNoteAction")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          aria-label={t("content.noteTitle")}
          placeholder={t("content.noteTitlePlaceholder")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Textarea
          aria-label={t("content.noteBody")}
          placeholder={t("content.noteBodyPlaceholder")}
          rows={6}
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </div>
    </Modal>
  );
}

/** Change a SharePoint source's selection. Opens on the current one (mount it only while open, so each open
 * starts from the source's selection); Save replaces it. */
export function EditSelectionModal({
  current,
  onClose,
  saving,
  onSave,
}: Readonly<{
  current: SharePointSelection | null;
  onClose: () => void;
  saving: boolean;
  onSave: (sel: SharePointSelection) => void;
}>) {
  const { t } = useTranslation("dataSources");
  // Null until the selection changes, so Save stays off for an untouched (or emptied) selection.
  const [selection, setSelection] = useState<SharePointSelection | null>(null);
  return (
    <Modal
      open
      onClose={onClose}
      title={t("content.editSelection")}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("content.cancel")}
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!selection}
            onClick={() => selection && onSave(selection)}
          >
            {t("content.saveSelection")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Text variant="body-sm" tone="secondary">
          {t("content.selectionHint")}
        </Text>
        <SharePointPicker initial={current} onSelect={setSelection} />
      </div>
    </Modal>
  );
}
